import { AlertTriangle, ArrowLeft, BookOpen, Check, FileText, LoaderCircle, Pencil, Plus, RotateCcw, Search, Sparkles, Trash2 } from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, Project, ResearchNote, ResearchNoteContent, ResearchNoteDraftRequest, Session, Task, User } from "../api";
import { isoDate, koStamp, koTime, noteStatusText } from "../lib/format";
import { ConfirmDialog, DataTable, Empty, Loading, ModalFrame, PageHead, RequirementHint, focusInvalidField, SpineField, TableFoot } from "../components/ui";
import { BlockEditor, BlockFile, BlockRenderer, DocumentBlock } from "../components/block-editor";

const asRecord = (value: unknown): Record<string, unknown> | null => value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;

type IdentityField = { key: string; label: string; value: string };
const identityLabels: Record<string, string> = {
  organization: "기관",
  department: "소속",
  jobTitle: "직책",
  project: "연구과제",
  projectCode: "과제 코드",
  managementNumber: "관리번호",
  principalInvestigator: "연구책임자",
};

function readableIdentityFields(snapshot: Record<string, unknown> | null, hasProject: boolean, hasManagementNumber: boolean): IdentityField[] {
  if (!snapshot) return [];
  const fields: IdentityField[] = [];
  const append = (key: string, value: unknown, parent?: string) => {
    if (value === null || value === undefined || value === "") return;
    if (Array.isArray(value)) {
      const rendered = value.filter((item) => ["string", "number", "boolean"].includes(typeof item)).join(", ");
      if (rendered) fields.push({ key: `${parent || "root"}.${key}`, label: identityLabels[key] || key, value: rendered });
      return;
    }
    const nested = asRecord(value);
    if (nested) {
      Object.entries(nested).forEach(([nestedKey, nestedValue]) => append(nestedKey, nestedValue, key));
      return;
    }
    if (parent === "author" && key === "name") return;
    if (hasProject && ((parent === undefined && key === "projectCode") || (parent === undefined && key === "project"))) return;
    if (hasManagementNumber && parent === undefined && key === "managementNumber") return;
    fields.push({
      key: `${parent || "root"}.${key}`,
      label: identityLabels[key] || key,
      value: String(value),
    });
  };
  Object.entries(snapshot).forEach(([key, value]) => append(key, value));
  return fields;
}

type NoteSectionType = NonNullable<ResearchNoteContent["sections"]>[number]["type"];
const noteSections: { type: NoteSectionType; label: string }[] = [
  { type: "BACKGROUND", label: "실험 목적 및 배경" },
  { type: "METHODS", label: "연구 내용 · 방법" },
  { type: "RESULTS", label: "실험 결과" },
  { type: "CONCLUSION", label: "결론" },
];

function sectionDocument(content: ResearchNoteContent): Record<NoteSectionType, DocumentBlock[]> {
  const result = {} as Record<NoteSectionType, DocumentBlock[]>;
  noteSections.forEach(({ type }) => {
    const section = content.sections.find((item) => item.type === type);
    result[type] = section?.blocks.map((block) => ({ ...block, file: block.file || undefined } as DocumentBlock)) || [];
  });
  return result;
}

function sectionText(blocks: DocumentBlock[]) {
  return blocks.filter((block): block is Extract<DocumentBlock, { type: "PARAGRAPH" }> => block.type === "PARAGRAPH")
    .map((block) => block.text.trim()).filter(Boolean).join("\n\n");
}

function NoteStatusBadge({ status, label }: { status: ResearchNote["status"]; label: string }) {
  return <span className={`state-cell note-${status.toLowerCase()}`}>
    {status === "REJECTED" ? <AlertTriangle size={14} aria-hidden="true" /> : <i />}{label}
  </span>;
}

export function NotesPage({ notes, draftRequests, tasks, projects, onOpen, onCreate, onRetry, canCreate = true }: {
  notes: ResearchNote[]; draftRequests: ResearchNoteDraftRequest[]; tasks: Task[]; projects: Project[];
  onOpen: (id: string) => void; onCreate: () => void; onRetry: (id: string) => Promise<void>; canCreate?: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  const projectOfTask = (task?: Task) => {
    const project = projects.find((item) => item.id === task?.projectId);
    return project ? project.name : task ? t("notes.standalone") : "-";
  };
  const projectOf = (note: ResearchNote) => projectOfTask(
    tasks.find((item) => item.id === note.sourceTaskIds[0]),
  );
  const needle = query.trim().toLowerCase();
  const visible = notes.filter((note) => {
    const version = note.versions.at(-1);
    return !needle || `${version?.content.title || ""} ${version?.content.authorName || ""}`.toLowerCase().includes(needle);
  });
  const pending = draftRequests.filter((request) => {
    if (request.status === "SUCCEEDED") return false;
    const sourceTitles = request.sourceTaskIds.map((id) => tasks.find((task) => task.id === id)?.internalTitle || "").join(" ");
    return !needle || `${sourceTitles} ${request.failureReason || ""}`.toLowerCase().includes(needle);
  });
  const rowCount = pending.length + visible.length;

  return <>
    <PageHead title={t("notes.title")} meta={t("notes.meta")} controls={<>
      <div className="search-field"><Search size={14} /><input aria-label={t("notes.search")} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("notes.searchPlaceholder")} /></div>
      {canCreate && <button type="button" className="primary" onClick={onCreate}><Plus size={15} /> {t("notes.create")}</button>}
    </>} />

    <div className="table-card">
      <div className="note-grid note-grid-head"><span>{t("notes.version")}</span><span>{t("notes.titleColumn")}</span><span>{t("notes.project")}</span><span data-col-optional="">{t("notes.author")}</span><span>{t("notes.authoredOn")}</span><span>{t("notes.status")}</span></div>
      {pending.map((request) => {
        const sources = request.sourceTaskIds.map((id) => tasks.find((task) => task.id === id)).filter((item): item is Task => Boolean(item));
        const title = sources.length > 1 ? t("notes.more", { title: sources[0].internalTitle, count: sources.length - 1 }) : sources[0]?.internalTitle || t("notes.draftTitle");
        const status = t(request.status === "QUEUED" ? "notes.queued" : request.status === "GENERATING" ? "notes.generating" : "notes.failed");
        const detail = request.status === "QUEUED" ? t("notes.queuedHelp") : request.status === "GENERATING" ? t("notes.generatingHelp") : request.failureReason || t("notes.failedHelp");
        return <div className={`note-grid note-grid-row pending-note-row ${request.status.toLowerCase()}`} key={request.id}>
          <span className="mono muted">—</span>
          <span className="title-cell"><b>{title}</b><small title={detail}>{detail}</small></span>
          <span>{projectOfTask(sources[0])}</span>
          <span data-col-optional="">{t("notes.auto")}</span>
          <span className="mono">{isoDate(request.createdAt)}</span>
          <span className="draft-row-status">
            {request.status === "GENERATING" ? <LoaderCircle size={13} className="spin" /> : request.status === "FAILED" ? <AlertTriangle size={13} /> : <i />}
            {status}
            {request.status === "FAILED" && <button type="button" aria-label={t("notes.retry", { title })} title={t("notes.retryTitle")} onClick={() => void onRetry(request.id)}><RotateCcw size={12} /></button>}
          </span>
        </div>;
      })}
      {visible.map((note) => {
        const version = note.versions.at(-1);
        return <button className="note-grid note-grid-row" key={note.id} onClick={() => onOpen(note.id)}>
          <span className="mono">v{note.currentVersion}</span>
          <span className="title-cell"><b>{version?.content.title || t("notes.untitled")}</b><small className="mono">{version?.contentSha256.slice(0, 12)}…</small></span>
          <span>{projectOf(note)}</span>
          <span data-col-optional="">{version?.content.authorName || "-"}</span>
          <span className="mono">{isoDate(version?.content.authoredOn) || "-"}</span>
          <NoteStatusBadge status={note.status} label={t(`noteStatus.${note.status}`, { defaultValue: noteStatusText[note.status] })} />
        </button>;
      })}
      {rowCount === 0 && <Empty title={t("notes.empty")} copy={t("notes.emptyHelp")} />}
      {rowCount > 0 && <TableFoot count={rowCount} total={notes.length + draftRequests.filter((request) => request.status !== "SUCCEEDED").length} hint={t("notes.listHelp")} />}
    </div>
  </>;
}

export function NoteDetail({ id, session, users, tasks, projects, onBack, onOpenTask, onChanged }: {
  id: string; session: Session; users: User[]; tasks: Task[]; projects: Project[];
  onBack: () => void; onOpenTask: (taskId: string) => void; onChanged: (message: string) => void;
}) {
  const [note, setNote] = useState<ResearchNote | null>(null);
  const [error, setError] = useState("");
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [confirmReview, setConfirmReview] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const load = useCallback(() => api.researchNote(id).then(setNote).catch((reason) => setError(reason instanceof Error ? reason.message : "불러오지 못했습니다.")), [id]);
  useEffect(() => { void load(); }, [load]);
  if (!note) return <main className="page"><button className="back" onClick={onBack}><ArrowLeft size={15} /> 연구노트</button>{error ? <div className="global-error">{error}</div> : <Loading layout="detail" label="연구노트를 불러오는 중" />}</main>;

  const version = note.versions.at(-1)!;
  const sourceIds = note.sourceTaskIds;
  const sourceTasks = sourceIds.map((sourceId) => tasks.find((task) => task.id === sourceId)).filter((task): task is Task => Boolean(task));
  const project = projects.find((item) => item.id === note.projectId) || projects.find((item) => item.id === sourceTasks[0]?.projectId);
  const identity = asRecord(version.content.identitySnapshot);
  const identityFields = readableIdentityFields(identity, Boolean(project), Boolean(note.managementNumber));
  const sectionDocuments = sectionDocument(version.content);
  const aiEvidence = version.generatedContent?.aiEvidence || [];
  const aiWarnings = version.generatedContent?.aiWarnings || [];
  const canFinalize = note.status === "DRAFT" && note.authorUserId === session.userId;
  const canReview = note.status === "AWAITING_REVIEW" && note.authorUserId !== session.userId && !!note.reviewPolicy.reviewerRole && session.roles.includes(note.reviewPolicy.reviewerRole);
  // 과거의 미완성 TSA 시점확인 기록은 데이터 호환을 위해 API에 남아 있을 수 있지만,
  // 사용자 서명 이력으로 제시하지 않는다.
  const signatureAttestations = note.attestations.filter((attestation) => attestation.action !== "ANCHOR");

  /** 확정 전 초안은 기록자가 새 버전으로 고칠 수 있다. 기존 버전은 덮어쓰지 않는다. */
  const canRevise = ["DRAFT", "REJECTED"].includes(note.status) && note.authorUserId === session.userId;
  const latestRejection = [...note.attestations].reverse().find((attestation) => attestation.action === "REJECT");

  async function signedAction(action: "finalize" | "review") {
    setBusy(true); setError("");
    try {
      if (action === "finalize") await api.finalizeResearchNote(note!.id); else await api.reviewResearchNote(note!.id);
      await load(); onChanged(action === "finalize" ? "기록자 확정을 남겼습니다." : "연구노트 점검을 승인했습니다.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "서명을 처리하지 못했습니다."); }
    finally { setBusy(false); }
  }
  async function reject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!rejectReason.trim()) return;
    setBusy(true); setError("");
    try {
      await api.rejectResearchNote(note!.id, rejectReason.trim());
      setRejectReason(""); setRejecting(false); await load(); onChanged("연구노트 보완을 요청했습니다.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "반려를 처리하지 못했습니다."); }
    finally { setBusy(false); }
  }
  async function attach(file: File) {
    setBusy(true); setError("");
    try { const stored = await api.uploadFile(file); setNote(await api.attachResearchNoteFile(note!.id, stored.id)); onChanged("첨부를 추가했습니다."); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "첨부를 추가하지 못했습니다."); }
    finally { setBusy(false); }
  }
  async function detach(fileId: string) {
    setBusy(true); setError("");
    try { setNote(await api.detachResearchNoteFile(note!.id, fileId)); onChanged("첨부를 제거했습니다."); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "첨부를 제거하지 못했습니다."); }
    finally { setBusy(false); }
  }
  async function downloadBlockFile(fileId: string, name: string) {
    try {
      const blob = await api.downloadFile(fileId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = name; link.click();
      URL.revokeObjectURL(url);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "파일을 내려받지 못했습니다."); }
  }

  return <main className="page note-detail-page">
    <div className="page-head detail-head">
      <div>
        <div className="breadcrumb"><button type="button" onClick={onBack}>← 연구노트</button><span className="mono">/ v{note.currentVersion}</span></div>
        <h1>{version.content.title}</h1>
        <div className="page-head-meta">
          {project && <><span>{project.name}</span><span className="dot">·</span></>}
          <span>{version.content.authorName}</span><span className="dot">·</span><span className="mono">{isoDate(version.content.authoredOn)}</span>
        </div>
      </div>
      <div className="page-head-controls">
        {canRevise && <button type="button" className="secondary" disabled={busy} onClick={() => setEditing(true)}><Pencil size={14} /> 초안 수정</button>}
      </div>
    </div>
    {error && <div className="global-error">{error}</div>}
    {note.versions.length > 1 && <details className="version-history">
      <summary>이전 버전 {note.versions.length - 1}개 · 수정 이력 보기</summary>
      <div>{note.versions.slice(0, -1).reverse().map((prior) => <article key={prior.id}><b>v{prior.version}</b><span>{prior.content.title}</span><code>{prior.contentSha256.slice(0, 12)}…</code><time className="mono">{koStamp(prior.createdAt)}</time></article>)}</div>
    </details>}

    <div className="note-detail-content">
      <div className="detail-main">
        <article className="paper note-identity-card">
          <div className="paper-head">
            <div><div className="paper-kind">식별 정보</div><div className="paper-meta mono">{version.contentSha256.slice(0, 16)}… · v{version.version}</div></div>
            <NoteStatusBadge status={note.status} label={noteStatusText[note.status]} />
          </div>
          <div className="spine note-identity-grid">
            {note.managementNumber && <SpineField label="[관리번호]"><span className="mono">{note.managementNumber}</span></SpineField>}
            {project && <>
              <SpineField label="[연구과제]">{project.name}</SpineField>
              <SpineField label="[과제 코드]"><span className="mono">{project.code}</span></SpineField>
            </>}
            {identityFields.map((field) => <SpineField label={`[${field.label}]`} key={field.key}>{field.value}</SpineField>)}
            <SpineField label="[기록자]">{version.content.authorName}</SpineField>
            <SpineField label="[연구기간]"><span className="mono">{isoDate(version.content.authoredOn)}</span></SpineField>
            <div className="note-identity-wide"><SpineField label="[관련 업무]">
              {sourceTasks.length
                ? <div className="link-list">{sourceTasks.map((task) => <button type="button" key={task.id} onClick={() => onOpenTask(task.id)}>{task.internalTitle}</button>)}</div>
                : <span className="muted">연결된 업무를 찾을 수 없습니다.</span>}
            </SpineField></div>
          </div>
        </article>

        {note.status === "REJECTED" && latestRejection && <div className="note-rejection-alert">
          <AlertTriangle size={18} /><div><b>점검에서 보완이 요청되었습니다.</b><p>{latestRejection.comment}</p></div>
        </div>}

        <article className="paper note-body-card">
          <div className="paper-head"><div><div className="paper-kind">연구 기록</div></div></div>
          <div className="note-block-sections">
            {noteSections.map((section) => <section key={section.type} className="note-block-section">
              <header><span>{section.label}</span></header>
              <BlockRenderer blocks={sectionDocuments[section.type]} loadImage={api.downloadFile} onDownloadFile={(fileId, name) => void downloadBlockFile(fileId, name)} />
            </section>)}
          </div>
        </article>

        {aiEvidence.length > 0 && <section className="section-card note-ai-evidence-card">
          <header><div><h2><BookOpen size={16} /> AI 초안 근거</h2><p>AI가 실제로 열어 본 원본과 생성 시점의 해시입니다.</p></div></header>
          <div className="note-ai-evidence-list">
            {aiEvidence.map((evidence) => {
              const taskSource = evidence.type === "SOURCE_TASK";
              return <article key={`${evidence.type}-${evidence.id}`}>
                <span className="note-evidence-kind">{taskSource ? "업무" : "프로젝트 문서"}</span>
                <div><b>{taskSource
                  ? <button type="button" onClick={() => onOpenTask(evidence.id)}>{evidence.title}</button>
                  : evidence.title}</b><p>{evidence.usedFor}</p></div>
                <code title={evidence.sourceSha256}>{evidence.sourceSha256.slice(0, 12)}…</code>
              </article>;
            })}
          </div>
          {aiWarnings.length > 0 && <div className="note-ai-warnings"><AlertTriangle size={15} /><div><b>연구자 확인 필요</b>{aiWarnings.map((warning) => <p key={warning}>{warning}</p>)}</div></div>}
        </section>}

        <section className="section-card note-attachments-card">
          <header><div><h2>첨부 파일</h2></div>
            {note.status === "DRAFT" && <label className="icon-button note-file-add" title="파일 추가" aria-label="파일 추가">{busy ? <LoaderCircle size={15} className="spin" /> : <Plus size={17} />}<input type="file" hidden aria-label="연구노트 첨부" disabled={busy} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void attach(file); }} /></label>}
          </header>
          <div className="file-list">
            {version.attachments.length === 0 && <p className="permission-note">첨부된 파일이 없습니다.</p>}
            {version.attachments.map((attachment) => <article key={attachment.file.id}>
              <span><b>{attachment.file.originalName}</b><small>{attachment.file.contentType} · {Math.round(attachment.file.sizeBytes / 1024)}KB · {koTime(attachment.attachedAt)}</small></span>
              {note.status === "DRAFT" && <button type="button" className="secondary" disabled={busy} onClick={() => void detach(attachment.file.id)}><Trash2 size={13} /> 제거</button>}
            </article>)}
          </div>
        </section>

        <section className="section-card note-signature-card">
          <header><div><h2>서명 이력</h2></div></header>
          <div className="note-table-scroll signature-table-scroll"><DataTable cols="minmax(160px, 1fr) minmax(120px, 1fr) 110px minmax(200px, 2fr)">
            <div className="data-row data-head"><span>시각</span><span>처리자</span><span>구분</span><span>내용</span></div>
            {signatureAttestations.length === 0 && <p className="permission-note">아직 확정 증적이 없습니다.</p>}
            {signatureAttestations.map((attestation) => <div className="data-row" key={attestation.id}>
              <span className="mono">{koStamp(attestation.createdAt)}</span>
              <span>{attestation.actorUserId ? users.find((user) => user.id === attestation.actorUserId)?.displayName || attestation.actorRole : "-"}</span>
              <span className={attestation.action === "REJECT" ? "rejection-action" : ""}>{attestation.action === "FINALIZE" ? "작성 완료" : attestation.action === "REJECT" ? "반려" : "점검 승인"}</span>
              <span>{attestation.comment || attestation.evidenceReference || `v${note.versions.find((item) => item.id === attestation.versionId)?.version || "-"} · ${attestation.contentSha256.slice(0, 12)}…`}</span>
            </div>)}
          </DataTable></div>
          {(canFinalize || canReview) && <div className="signature-panel">
            <div><b>{canFinalize ? "기록자 서명" : "연구노트 점검"}</b>
              <p>{canFinalize ? "현재 로그인한 기록자의 서명으로 이 버전을 확정합니다." : `${note.reviewPolicy.reviewerRole} 권한으로 승인하거나 보완을 요청할 수 있습니다.`}</p>
            </div>
            {canFinalize
              ? <button className="primary" disabled={busy} onClick={() => setConfirmFinalize(true)}>서명하기</button>
              : <div className="review-actions"><button className="danger-outline" disabled={busy} onClick={() => setRejecting(true)}>반려</button>
                <button className="primary" disabled={busy} onClick={() => setConfirmReview(true)}>승인</button></div>}
          </div>}
        </section>

      </div>
    </div>

    {editing && <ResearchNoteEditModal note={note} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await load(); onChanged("연구노트 초안의 새 버전을 저장했습니다."); }} />}
    {confirmFinalize && <ConfirmDialog title="연구노트에 서명할까요?" eyebrow="AUTHOR SIGNATURE" confirmLabel="서명 확정" busy={busy} onClose={() => setConfirmFinalize(false)} onConfirm={async () => { await signedAction("finalize"); setConfirmFinalize(false); }}>
      <p>현재 버전의 내용과 첨부 파일이 기록자 서명과 함께 고정됩니다. 점검에서 반려되면 새 버전으로 보완할 수 있습니다.</p>
    </ConfirmDialog>}
    {confirmReview && <ConfirmDialog title="연구노트 점검을 승인할까요?" eyebrow="REVIEW" confirmLabel="승인" busy={busy} onClose={() => setConfirmReview(false)} onConfirm={async () => { await signedAction("review"); setConfirmReview(false); }}>
      <p>현재 로그인한 계정으로 승인 이력이 기록되고 담당자에게 알림이 전송됩니다.</p>
    </ConfirmDialog>}
    {rejecting && <ModalFrame title="연구노트 반려" eyebrow="REVISION REQUEST" onClose={() => !busy && setRejecting(false)}><form className="modal-form note-reject-form" onSubmit={reject}>
      <label className="span-2"><span className="note-reject-label">반려 사유 <em aria-hidden="true">*</em></span><textarea autoFocus required maxLength={2000} rows={6} value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} placeholder="담당자가 보완할 내용을 구체적으로 적어주세요." /></label>
      <p className="permission-note span-2">반려 사유와 처리자·시각이 이력에 남고 담당자에게 알림이 전송됩니다.</p>
      <footer className="span-2"><button type="button" disabled={busy} onClick={() => setRejecting(false)}>취소</button><button className="danger" disabled={busy || !rejectReason.trim()}>{busy ? "처리 중…" : "반려"}</button></footer>
    </form></ModalFrame>}
  </main>;
}

export function ResearchNoteCreateModal({ task, canConvertNote, tasks, onClose, onCreated }: {
  task?: Task; canConvertNote: (task: Task) => boolean; tasks: Task[]; onClose: () => void; onCreated: () => Promise<void>;
}) {
  const [managementNumber, setManagementNumber] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [sourceTaskIds, setSourceTaskIds] = useState<string[]>(task && canConvertNote(task) ? [task.id] : []);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const eligible = [
    ...(task ? [task] : []),
    ...tasks.filter((item) => item.id !== task?.id),
  ].filter(canConvertNote);
  const visible = eligible.filter((item) => !query.trim() || item.internalTitle.toLowerCase().includes(query.trim().toLowerCase()));

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAttempted(true);
    if (!sourceTaskIds.length) { focusInvalidField(event.currentTarget); return; }
    if (sourceTaskIds.some((id) => !eligible.some((item) => item.id === id))) { setError("변환 권한이 변경되었습니다. 원본 업무를 다시 선택해 주세요."); return; }
    setBusy(true); setError("");
    try { await api.requestResearchNoteDraft(sourceTaskIds, managementNumber); await onCreated(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "초안 생성을 요청하지 못했습니다."); }
    finally { setBusy(false); }
  }

  return <ModalFrame title="연구노트 초안 생성" eyebrow="EVIDENCE TO DRAFT" onClose={onClose}><form className="modal-form note-create-flow" onSubmit={submit}>
    <div className="draft-intro span-2"><Sparkles size={18} /><div><b>완료된 작업 기록을 초안으로 재구성합니다.</b><p>원본 지시서와 결과 보고는 바꾸지 않습니다. 작업 근거를 바탕으로 배경·방법·결과·결론을 정리하며, 준비된 초안은 확정 전까지 수정할 수 있습니다.</p></div></div>
    <div className="source-toolbar span-2"><div className="search-field"><Search size={14} /><input aria-label="완료 업무 검색" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="변환 가능한 완료 업무 검색" /></div><span><b>{sourceTaskIds.length}</b>개 선택</span></div>
    <fieldset className={`span-2 source-picker source-picker-rich ${attempted && !sourceTaskIds.length ? "field-missing" : ""}`}><legend>초안에 포함할 원본 업무 <em className="required-mark">필수</em></legend>
      {visible.map((item) => {
        const selected = sourceTaskIds.includes(item.id);
        return <label className={selected ? "selected" : ""} key={item.id}>
          <input type="checkbox" checked={selected} onChange={(event) => setSourceTaskIds((current) => event.target.checked ? [...current, item.id] : current.filter((id) => id !== item.id))} />
          <span className="source-check">{selected && <Check size={12} />}</span><FileText size={16} />
          <span><b>{item.internalTitle}</b><small>완료 보고 {item.rounds.filter((round) => round.report).length}건</small></span>
        </label>;
      })}
      {visible.length === 0 && <div className="source-empty">선택할 수 있는 완료 작업물이 없습니다.</div>}
    </fieldset>
    <label className="span-2">관리번호 <small>선택 사항</small><input value={managementNumber} onChange={(event) => setManagementNumber(event.target.value)} placeholder="예: RND-2026-014" maxLength={80} /></label>
    <div className="mcp-handoff span-2"><span className="draft-pulse" /><p><b>요청하면 연구노트 목록에 생성 상태가 표시됩니다.</b><small>완료되면 같은 목록에서 초안을 열어 검토하고 수정할 수 있습니다.</small></p></div>
    {attempted && <RequirementHint className="span-2" missing={sourceTaskIds.length ? [] : ["원본 업무 1개 이상"]} />}
    {error && <div className="error-box span-2">{error}</div>}
    <footer className="span-2"><button type="button" onClick={onClose}>취소</button><button className="primary" disabled={busy}>{busy ? "요청 중…" : <><Sparkles size={14} /> 초안 생성 요청</>}</button></footer>
  </form></ModalFrame>;
}


function ResearchNoteEditModal({ note, onClose, onSaved }: { note: ResearchNote; onClose: () => void; onSaved: () => Promise<void> }) {
  const version = note.versions.at(-1)!;
  const current = version.content;
  const generated = version.generatedContent;
  const [content, setContent] = useState({ ...current });
  const [sections, setSections] = useState(() => sectionDocument(current));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const field = (name: "title" | "authoredOn" | "authorName", value: string) => setContent((before) => ({ ...before, [name]: value }));
  const updateSection = (type: NoteSectionType, blocks: DocumentBlock[]) => setSections((before) => ({ ...before, [type]: blocks }));
  const uploadImage = async (file: File): Promise<BlockFile> => {
    if (!file.type.startsWith("image/")) throw new Error("이미지 파일만 본문에 삽입할 수 있습니다.");
    return api.uploadFile(file);
  };
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    const missing = noteSections.find((section) => !sectionText(sections[section.type]));
    if (missing) { setError(`${missing.label}에 한 개 이상의 문단을 입력해 주세요.`); return; }
    const invalidTable = Object.values(sections).flat().find((block) => block.type === "TABLE" && (block.columns.length === 0 || block.columns.some((column) => !column.trim()) || new Set(block.columns.map((column) => column.trim())).size !== block.columns.length));
    if (invalidTable) { setError("표의 열 이름은 비어 있지 않고 서로 달라야 합니다."); return; }
    const storedSections = noteSections.map((section) => ({ type: section.type, blocks: sections[section.type] }));
    const nextContent: ResearchNoteContent = {
      ...content,
      sections: storedSections,
    };
    setBusy(true);
    try { await api.reviseResearchNote(note.id, nextContent); await onSaved(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "새 버전을 저장하지 못했습니다."); }
    finally { setBusy(false); }
  }
  return <ModalFrame title="연구노트 초안 수정" eyebrow={`CREATE VERSION ${note.currentVersion + 1}`} onClose={onClose}><form className="modal-form block-document-form" onSubmit={submit}>
    <div className="policy-note span-2">기존 버전은 덮어쓰지 않습니다. 저장하면 내용 해시가 다른 새 버전이 추가됩니다.</div>
    {generated && <div className="ai-trace-legend span-2"><Sparkles size={17} /><div><b>AI 초안 출처를 추적하고 있습니다.</b><p>초록색 블록은 AI 원문이 유지된 내용입니다. 수정하면 사용자 작성 블록으로 전환됩니다.</p></div></div>}
    <label className={`span-2 ${generated?.title === content.title ? "ai-origin-input" : ""}`}>제목 <span>{generated?.title === content.title ? "AI 원문" : ""}</span><input aria-label="수정 제목" value={content.title} onChange={(event) => field("title", event.target.value)} required maxLength={240} /></label>
    <label>작성일<input aria-label="수정 작성일" type="date" value={content.authoredOn} onChange={(event) => field("authoredOn", event.target.value)} required /></label>
    <label>기록자<input aria-label="수정 기록자" value={content.authorName} onChange={(event) => field("authorName", event.target.value)} required maxLength={120} /></label>
    <div className="note-section-editors span-2">{noteSections.map((section) => <section className="note-section-editor" key={section.type}>
      <header><b>{section.label}</b><small>한 문서처럼 작성하고 / 또는 상단 도구로 표·이미지를 추가하세요.</small></header>
      <BlockEditor ariaLabel={section.label} value={sections[section.type]} onChange={(blocks) => updateSection(section.type, blocks)} onUploadImage={uploadImage} loadImage={api.downloadFile} onError={setError} />
    </section>)}</div>
    {error && <div className="error-box span-2">{error}</div>}
    <footer className="span-2"><button type="button" onClick={onClose}>취소</button><button className="primary" disabled={busy}>{busy ? "저장 중…" : "새 버전 저장"}</button></footer>
  </form></ModalFrame>;
}
